import { useId, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@nexus/ui";
import { SettingsRow } from "../shared/SettingsRow";

/**
 * The shared chip input, sized like the other onboarding fields: 36px tall, with the same 13px text
 * and placeholder grey as the text inputs.
 */
export const ONBOARDING_TAG_INPUT_CLASS = "min-h-9 [&_input]:text-[13px] [&_input]:placeholder:text-muted-foreground";

/** Spans every field column: long text, checkbox groups, the naming convention card, messages. */
export const ONBOARDING_FULL_ROW_CLASS = "col-span-full";

/** Two aligned columns for short fields, stacking on narrow screens; long fields span the row. */
const ONBOARDING_FIELD_GRID_CLASS = "grid gap-x-6 gap-y-5 @md:grid-cols-2";

/**
 * An onboarding section in the Organization tab layout: title and hint on the left, fields on the
 * right. Fields sit in two columns once their area is wide enough (Edit organization) and in one
 * column otherwise (New organization, narrow screens).
 */
export function OnboardingSection({
  title,
  hint,
  children,
  className,
  "data-testid": testId,
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  /** New organization passes `px-0`: its step body already has the side padding. */
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <SettingsRow title={title} hint={hint} className={className} data-testid={testId}>
      <div className="@container">
        <div className={ONBOARDING_FIELD_GRID_CLASS}>{children}</div>
      </div>
    </SettingsRow>
  );
}

/**
 * An optional onboarding section that starts collapsed. Its fields stay mounted while hidden, so
 * values and field ids survive, and it stays open while one of its fields has an error.
 */
export function OnboardingCollapsibleSection({
  title,
  summary,
  hasError = false,
  children,
  className,
  "data-testid": testId,
}: {
  title: ReactNode;
  /** Shown beside the title while collapsed, e.g. how many fields are filled. */
  summary?: ReactNode;
  hasError?: boolean;
  children: ReactNode;
  /** New organization passes `px-0`: its step body already has the side padding. */
  className?: string;
  "data-testid"?: string;
}) {
  const contentId = useId();
  const [isOpen, setIsOpen] = useState(false);
  // Derived, not synced in an effect, so an invalid field is visible in the same render that reports it.
  const isExpanded = isOpen || hasError;

  return (
    <div className={cn("border-b border-border-subtle px-8 py-3", className)} data-testid={testId}>
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        disabled={hasError}
        onClick={() => setIsOpen((open) => !open)}
        className="flex min-h-11 w-full items-center gap-2 rounded-md py-1.5 text-left text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/40"
      >
        <ChevronRight
          className={cn("size-3.5 shrink-0 transition-transform", isExpanded && "rotate-90")}
          aria-hidden
        />
        <span className="text-[13px] font-semibold">{title}</span>
        {summary ? <span className="ml-auto text-[11px] text-muted-foreground">{summary}</span> : null}
      </button>
      {/* Lines the fields up with the field column of the non-collapsible sections (220px heading + 32px gap). */}
      <div id={contentId} hidden={!isExpanded} className="pb-3 pt-3 sm:pl-[252px]">
        <div className="@container">
          <div className={ONBOARDING_FIELD_GRID_CLASS}>{children}</div>
        </div>
      </div>
    </div>
  );
}
